import { SPACING as LAYOUT_SPACING } from "@/config/layout";
import { LIST_TABLE_FOOTER_STACK, SPACING } from "@/lib/constants/workspace-layout";
import { cn } from "@/lib/utils";

/** Shared typography scale (aligns with shell page titles; use for visible headings outside chrome). */
export const TYPE = {
  pageTitle: "text-2xl font-semibold tracking-tight text-foreground",
  sectionTitle: "text-lg font-semibold tracking-tight text-foreground",
  body: "text-sm leading-relaxed text-foreground",
  bodyMuted: "text-sm leading-relaxed text-muted-foreground",
  caption: "text-xs text-muted-foreground",
} as const;

export const CARD_BASE = "surface-card text-card-foreground rounded-lg border";

/** Document preview stage — themed shell with light document paper for readable dark-mode previews. */
export const DOCUMENT_VIEWER_STAGE_CLASS =
  "min-h-[min(68dvh,40rem)] flex-1 overflow-auto overscroll-contain rounded-md border border-border bg-muted/20 dark:bg-muted/30";
export const DOCUMENT_VIEWER_PAPER_CLASS = "bg-white text-neutral-900 shadow-sm";
export const DOCUMENT_VIEWER_HTML_INJECT_STYLE =
  "<style>html{color-scheme:light;background:#fff;color:#171717}body{background:#fff;color:#171717;margin:0;padding:16px;line-height:1.5;font-family:system-ui,sans-serif}.nation{white-space:nowrap}</style>";

export const SECTION_TITLE = "text-sm font-semibold leading-tight text-foreground";
export const SECTION_LABEL = "text-xs font-medium tracking-tight text-muted-foreground";
export const COMPACT_SECTION_LABEL = "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
export const TEXT_NANO = "text-[9px]";
export const TEXT_MICRO = "text-[10px]";
export const TEXT_CAPTION = "text-[11px]";
export const TEXT_BODY_XS = "text-xs";
/** Shared workspace detail shell — one elevated card; compact header/body/sections inside. */
export const WORKSPACE_DETAIL = {
  ROOT: "flex min-h-0 min-w-0 flex-col",
  SHELL: "surface-card flex min-h-0 min-w-0 flex-col gap-3 overflow-y-auto overflow-x-hidden rounded-md border p-3",
  HEADER: "flex min-w-0 items-start gap-2 border-b border-border/60 pb-2",
  BACK: "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border hover:bg-muted",
  HEADER_BODY: "min-w-0 flex-1 space-y-1",
  TITLE: "truncate text-sm font-semibold leading-tight text-foreground",
  SUBTITLE: "truncate text-xs text-muted-foreground",
  META: "flex flex-wrap items-center gap-1",
  ACTIONS: "flex shrink-0 flex-wrap items-center justify-end gap-1",
  BODY: "flex min-w-0 flex-col gap-3",
  SECTION: "space-y-2",
  /** Nested business panel — border shell; title sits on the top border (legend cut). */
  PANEL: "relative space-y-2 rounded-md border border-border bg-card px-2.5 pb-2.5 pt-3.5",
  /** Outer stage shell that hosts nested legend panels. */
  PANEL_STAGE: "relative space-y-3 rounded-md border border-border bg-card p-3 pt-4",
  /** Nested column label inside a parent PANEL (no second border/legend). */
  COLUMN_LABEL: "text-[11px] font-semibold leading-none text-muted-foreground",
  /** Absolute legend centered on PANEL top border. */
  TITLE_LINE: "absolute left-2 right-2 top-0 z-[1] flex min-w-0 -translate-y-1/2 items-center gap-1.5",
  TITLE_LINE_RULE: "hidden",
  /** No truncate/overflow-hidden — clips glyph edges on border legend. */
  TITLE_LINE_LABEL:
    "min-w-0 max-w-[min(100%,16rem)] whitespace-nowrap bg-card px-1.5 py-0.5 text-xs font-semibold leading-normal text-foreground",
  TITLE_LINE_ACTION: "ml-auto flex shrink-0 items-center gap-1.5 bg-card pl-0.5",
  /** Shared control height for party/detail toolbars (buttons + inputs). */
  CTRL: "h-8 gap-1 px-2.5 text-xs",
  ICON_CTRL: "h-8 w-8 shrink-0 p-0",
  SECTION_HEAD: "flex min-w-0 items-center justify-between gap-2",
  SECTION_LABEL: COMPACT_SECTION_LABEL,
  TAB_BODY: "surface-card rounded-md border p-2",
  ROW_LIST: "divide-y divide-border/60 rounded-md border border-border/60",
  ROW: "flex min-w-0 items-start justify-between gap-2 px-2 py-1.5 text-xs",
  ROW_MAIN: "min-w-0 flex-1",
  ROW_TITLE: "truncate font-medium text-foreground",
  ROW_META: "mt-0.5 text-[11px] leading-snug text-muted-foreground",
  ROW_ACTIONS: "flex shrink-0 flex-wrap items-center gap-1",
  TAB_STRIP: "flex min-w-0 flex-wrap items-center gap-1 border-b border-border/60 pb-1",
  TAB: "inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium transition-colors",
  TAB_ON: "bg-primary/10 text-foreground",
  TAB_OFF: "text-muted-foreground hover:bg-muted hover:text-foreground",
  /** Lifecycle workflow — equal circles; rail segments between nodes (no overlay gap). */
  TIMELINE: "relative w-full min-w-0 py-1",
  TIMELINE_RAIL: "hidden",
  TIMELINE_RAIL_PROGRESS: "hidden",
  TIMELINE_STEP:
    "relative z-[1] flex min-w-0 flex-1 flex-col items-center gap-1.5",
  TIMELINE_STEP_BTN:
    "flex w-full min-w-0 flex-col items-center gap-1.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40",
  TIMELINE_NODE_ROW: "relative flex h-[22px] w-full items-center justify-center",
  TIMELINE_SEG:
    "pointer-events-none absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-muted-foreground/30",
  TIMELINE_SEG_LEFT: "-left-px right-[calc(50%-1px)]",
  TIMELINE_SEG_RIGHT: "left-[calc(50%-1px)] -right-px",
  TIMELINE_SEG_DONE: "bg-emerald-500",
  TIMELINE_SEG_HIDDEN: "hidden",
  TIMELINE_DOT:
    "relative z-[1] inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold leading-none transition-[background-color,border-color,color,transform] duration-150 motion-reduce:transition-none",
  TIMELINE_DOT_DONE:
    "border-emerald-600 bg-emerald-600 text-white dark:border-emerald-500 dark:bg-emerald-500",
  TIMELINE_DOT_CURRENT:
    "scale-105 border-sky-500 bg-sky-600 text-white dark:border-sky-400 dark:bg-sky-500 motion-reduce:scale-100",
  TIMELINE_DOT_TODO:
    "border-muted-foreground/40 bg-card text-muted-foreground",
  TIMELINE_DOT_SKIP:
    "border-dashed border-muted-foreground/50 bg-card text-muted-foreground/55",
  TIMELINE_LINE: "hidden",
  TIMELINE_LINE_DONE: "",
  TIMELINE_LINE_CURRENT: "",
  TIMELINE_LABEL:
    "max-w-full text-center text-[10px] font-medium leading-tight text-muted-foreground line-clamp-2 transition-colors duration-150 motion-reduce:transition-none",
  TIMELINE_LABEL_CURRENT: "font-semibold text-sky-700 dark:text-sky-300",
  TIMELINE_LABEL_DONE: "text-emerald-700 dark:text-emerald-400",
  /** Primary lifecycle cluster — slight elevation vs secondary panels. */
  PANEL_PRIMARY:
    "relative space-y-2 rounded-md border border-border bg-card px-2.5 pb-2.5 pt-3.5 shadow-sm ring-1 ring-sky-500/15 dark:ring-sky-400/20",
  /** Compact sticky apply/decision column on proposal workspace. */
  DECISION_RAIL:
    "space-y-2 rounded-md border border-border bg-card/95 px-2.5 py-2 lg:sticky lg:top-2 lg:col-span-4 lg:self-start lg:backdrop-blur-sm",
  DECISION_RAIL_META: "flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground",
  DECISION_RAIL_FOOTER: "flex items-center justify-end gap-1 border-t border-border/60 pt-2",
  /** Matrix attention row accent — status semantics only, not risk/confidence. */
  MATRIX_ATTENTION_ROW:
    "border-b border-border/60 align-top border-l-2 border-l-amber-500/70 bg-amber-500/[0.04] dark:border-l-amber-400/60 dark:bg-amber-500/[0.06]",
  MATRIX_SETTLED_ROW: "border-b border-border/60 align-top",
  /** Controlled disclosure header + reveal (120–160ms). */
  DISCLOSURE_SUMMARY:
    "flex cursor-pointer list-none items-center justify-between gap-2 px-2.5 py-2 text-[11px] text-foreground marker:content-none [&::-webkit-details-marker]:hidden",
  DISCLOSURE_CHEVRON:
    "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-150 motion-reduce:transition-none",
  DISCLOSURE_CHEVRON_OPEN: "rotate-180",
  DISCLOSURE_PANEL:
    "grid transition-[grid-template-rows] duration-150 ease-out motion-reduce:transition-none",
  DISCLOSURE_PANEL_OPEN: "grid-rows-[1fr]",
  DISCLOSURE_PANEL_CLOSED: "grid-rows-[0fr]",
  DISCLOSURE_PANEL_INNER: "min-h-0 overflow-hidden",
} as const;
export const SECTION_SUBTITLE = "mt-0.5 text-xs text-muted-foreground";
/** Form / detail shells on canvas — white elevated card (not muted gray). */
export const FORM_PANEL_SHELL = "surface-card rounded-md border p-3";
export const DETAIL_HEADER_PANEL = "surface-card rounded-md border p-2.5";
export const DETAIL_SECTION_PANEL = "surface-card rounded-md border p-2";
export const DETAIL_META_CHIP =
  "surface-control inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs";
export const FORM_GROUP_LABEL = "text-xs font-medium tracking-tight text-muted-foreground";
export const FORM_FIELD_LABEL = "text-xs font-medium text-muted-foreground";
export const FORM_HELPER_TEXT = "text-xs text-muted-foreground";
/** Nested helper strip — white surface (Avalon: no gray business wells on canvas). */
export const BOX_MUTED = `surface-card rounded-lg border ${SPACING.CARD_CONTENT_PADDING}`;
export const BOX_SUCCESS = `rounded-lg border border-border bg-success/10 ${SPACING.CARD_CONTENT_PADDING}`;
export const BOX_DESTRUCTIVE = `rounded-lg border border-destructive/25 bg-destructive/10 ${SPACING.CARD_CONTENT_PADDING}`;
export const CODE_BLOCK = `surface-card overflow-auto rounded-lg border text-xs text-foreground ${SPACING.CARD_CONTENT_PADDING}`;

/** Long URLs and pre-wrapped mail bodies: wrap inside panel width; no horizontal scrollbar. */
export const THREAD_MESSAGE_BODY =
  "min-w-0 max-w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere]";

/** One message in thread: grow to full body height (no inner clip). */
export const THREAD_PANEL_SINGLE =
  "flex min-w-0 w-full max-w-full flex-col gap-3 overflow-x-hidden";

/** Multiple messages: stack in the page; the workspace shell is the scroller. */
export const THREAD_SCROLL_PANEL =
  "flex min-w-0 w-full max-w-full flex-col gap-3 overflow-x-hidden [&>*]:shrink-0";

/** Thread and original body: full height in the page, no inner vertical scroller. */
export const THREAD_BODY_PANEL = "min-w-0 max-w-full overflow-x-hidden";
/** Sole FE select skin — used only via SearchableSelect; do not style raw `<select>` or ui/select. */
export const SELECT_BASE =
  "surface-control h-8 min-w-[100px] rounded-md border border-border/80 bg-transparent px-2.5 py-1 text-xs leading-5 text-foreground transition-[border-color,box-shadow,background-color] duration-150 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40";
export const SELECT_BASE_COMPACT =
  "surface-control h-9 min-w-[100px] rounded-md border border-border/80 bg-transparent px-2.5 py-1.5 text-xs leading-normal text-foreground transition-[border-color,box-shadow,background-color] duration-150 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40";

/** SearchableSelect trigger (sole FE select SoT). */
export const WORKSPACE_SEARCHABLE_SELECT_TRIGGER_CLASS = cn(
  SELECT_BASE,
  "h-auto min-h-8 min-w-[10rem] max-w-full shrink-0 justify-between gap-2 py-1 font-normal tracking-tight",
  "hover:border-foreground/20 hover:bg-muted/30",
  "data-[state=open]:border-foreground/25 data-[state=open]:bg-muted/35 data-[state=open]:shadow-sm data-[state=open]:ring-1 data-[state=open]:ring-foreground/10",
);

/** @deprecated Use {@link WORKSPACE_SEARCHABLE_SELECT_TRIGGER_CLASS} */
export const WORKSPACE_LIST_FILTER_SELECT_CLASS = WORKSPACE_SEARCHABLE_SELECT_TRIGGER_CLASS;

export const WORKSPACE_SEARCHABLE_SELECT_LAYOUT_CLASS = {
  filter: "",
  /** Toolbar filters: fixed control height, readable min width, wrap instead of collapsing. */
  toolbar: cn(LAYOUT_SPACING.CONTROL_HEIGHT_SM_MIN, "w-auto shrink-0 min-w-[9rem] max-md:w-full"),
  form: cn(LAYOUT_SPACING.INPUT_HEIGHT, "min-h-9 w-full min-w-0"),
  /** Dense form row aligned to WORKSPACE_DETAIL.CTRL (h-8). */
  dense: cn(LAYOUT_SPACING.CONTROL_HEIGHT_SM_MIN, "w-full min-w-0 py-0"),
  compact: cn(LAYOUT_SPACING.CONTROL_HEIGHT_XS, TEXT_CAPTION, "py-0.5 leading-tight"),
  pageSize: cn(LAYOUT_SPACING.CONTROL_HEIGHT_SM_MIN, "w-auto shrink-0 min-w-[5.75rem]"),
} as const;

/** SearchableSelect popover shell (Linear-style panel). */
export const WORKSPACE_SEARCHABLE_SELECT_PANEL_CLASS =
  "z-[130] w-[var(--radix-popover-trigger-width)] min-w-[max(12rem,var(--radix-popover-trigger-width))] max-w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border/70 bg-popover p-0 shadow-xl shadow-black/5 pointer-events-auto";

/** Option row inside SearchableSelect panel. */
export const WORKSPACE_SEARCHABLE_SELECT_ITEM_CLASS =
  "scroll-mt-0 cursor-pointer items-start gap-2 rounded-md px-2.5 py-1.5 text-xs leading-snug transition-colors duration-100 aria-selected:bg-muted/70";

/** Clear-all row — muted vs real options. */
export const WORKSPACE_SEARCHABLE_SELECT_CLEAR_ITEM_CLASS =
  "scroll-mt-0 cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground transition-colors duration-100 aria-selected:bg-muted/50";

export const KPI_METRIC_CARD_BASE =
  "surface-card-metric flex h-full min-h-[5rem] min-w-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-xl px-2.5 py-3 text-center";
export const KPI_METRIC_CARD_PREMIUM = cn(
  KPI_METRIC_CARD_BASE,
  "transition-colors duration-150 hover:border-primary/35",
);
/** Interactive KPI card (`<Link>` / `<a>`): fade on hover so it reads as navigable. */
export const KPI_METRIC_CARD_LINK =
  "gap-1 transition-opacity duration-150 hover:opacity-75 active:opacity-65 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50";

/** Static metric card surfaces (display-only `<div>`). */
export const KPI_METRIC_CARD_VARIANT = {
  /** Keep white card + elevation from surface-card (no muted wash). */
  default: "",
  /** @deprecated Prefer {@link WORK_STATUS_TONE.overdue.metricCard} — kept for inbox/rejected alerts. */
  danger: "border-destructive/30 bg-destructive/5",
  warning: "border-warning/30 bg-warning/5",
  success: "border-success/30 bg-success/5",
  info: "border-info/30 bg-info/5",
} as const;

/** Semantic hub status colors — use across Dashboard, Projects, Tasks KPIs, pills, and badges. */
export const WORK_STATUS_TONE = {
  neutral: {
    metricCard: "surface-card-metric border-border",
    pill: "surface-control border-border text-foreground hover:border-ring/50",
    icon: "text-muted-foreground",
    badge: "surface-control border-border text-foreground",
    strip: "bg-muted",
  },
  pending: {
    metricCard: "surface-card-metric border-amber-500/30 bg-amber-500/[0.06]",
    pill: "border-amber-600/40 bg-amber-500/15 text-amber-900 hover:bg-amber-500/25 dark:border-amber-500/35 dark:bg-amber-500/10 dark:text-amber-200",
    icon: "text-amber-600 dark:text-amber-200",
    badge: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:border-amber-500/35 dark:bg-amber-500/15 dark:text-amber-200",
    strip: "bg-amber-500/80",
  },
  inProgress: {
    metricCard: "surface-card-metric border-violet-500/30 bg-violet-500/[0.06]",
    pill: "border-violet-600/40 bg-violet-500/15 text-violet-900 hover:bg-violet-500/25 dark:border-violet-500/35 dark:bg-violet-500/10 dark:text-violet-200",
    icon: "text-violet-600 dark:text-violet-300",
    badge: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:border-violet-500/35 dark:bg-violet-500/15 dark:text-violet-200",
    strip: "bg-violet-500/80",
  },
  atRisk: {
    metricCard: "surface-card-metric border-orange-500/60 bg-orange-500/[0.06]",
    pill: "border-orange-600/50 bg-orange-500/15 text-orange-950 hover:bg-orange-500/25 dark:border-orange-500/60 dark:bg-orange-500/10 dark:text-orange-200",
    icon: "text-orange-600 dark:text-orange-400",
    badge: "border-orange-500/50 bg-orange-500/10 text-orange-700 dark:border-orange-500/55 dark:bg-orange-500/15 dark:text-orange-200",
    strip: "bg-orange-500/80",
  },
  overdue: {
    metricCard: "surface-card-metric border-destructive/30 bg-destructive/[0.06]",
    pill: "border-destructive/35 bg-destructive/10 text-destructive hover:bg-destructive/20",
    icon: "text-destructive/85",
    badge: "border-destructive/30 bg-destructive/10 text-destructive dark:border-destructive/40 dark:bg-destructive/20 dark:text-destructive",
    strip: "bg-destructive",
  },
  success: {
    metricCard: "surface-card-metric border-emerald-500/30 bg-emerald-500/[0.06]",
    pill: "border-emerald-600/40 bg-emerald-500/15 text-emerald-950 hover:bg-emerald-500/25 dark:border-emerald-500/35 dark:bg-emerald-500/10 dark:text-emerald-200",
    icon: "text-emerald-600 dark:text-emerald-200",
    badge: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/18 dark:text-emerald-200",
    strip: "bg-emerald-500/80",
  },
  info: {
    metricCard: "border-sky-500/30 bg-sky-500/[0.06]",
    pill: "border-sky-600/40 bg-sky-500/15 text-sky-950 hover:bg-sky-500/25 dark:border-sky-500/35 dark:bg-sky-500/10 dark:text-sky-200",
    icon: "text-sky-600 dark:text-sky-200",
    badge: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:border-sky-500/35 dark:bg-sky-500/15 dark:text-sky-200",
    strip: "bg-sky-500/80",
  },
  inbox: {
    metricCard: "border-warning/30 bg-warning/5",
    pill: "border-warning/40 bg-warning/10 text-warning hover:bg-warning/20 dark:border-warning/40 dark:bg-warning/20 dark:text-warning",
    icon: "text-warning dark:text-warning",
    badge: "border-warning/30 bg-warning/10 text-warning dark:border-warning/30 dark:bg-warning/20 dark:text-warning",
    strip: "bg-warning/80",
  },
} as const;

export type WorkStatusTone = keyof typeof WORK_STATUS_TONE;

export function workStatusMetricCardClass(tone: WorkStatusTone, active = false): string {
  const base = WORK_STATUS_TONE[tone].metricCard;
  if (!active) return base;
  if (tone === "atRisk") return cn(base, "border-orange-500 bg-orange-500/[0.12]");
  if (tone === "overdue") return cn(base, "border-destructive/55 bg-destructive/[0.12]");
  if (tone === "success") return cn(base, "border-emerald-500/45 bg-emerald-500/[0.12]");
  if (tone === "neutral") return cn(base, "border-primary/45 bg-primary/[0.12]");
  return base;
}

export function workStatusPillClass(tone: WorkStatusTone): string {
  return cn(
    "inline-flex max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-ring/40",
    WORK_STATUS_TONE[tone].pill,
  );
}

export function workStatusIconClass(tone: WorkStatusTone): string {
  return cn("h-3 w-3 shrink-0", WORK_STATUS_TONE[tone].icon);
}

export function workStatusBadgeClass(tone: WorkStatusTone): string {
  return cn(STATUS_BADGE_BASE, WORK_STATUS_TONE[tone].badge);
}

export function riskCardToneClass(resolutionStatus: string, escalated: boolean): string {
  if (resolutionStatus === "resolved") {
    return "border-success/35 bg-success/[0.06]";
  }
  if (escalated) {
    return "border-destructive/40 bg-destructive/[0.06]";
  }
  return "";
}

export const STRATEGY_HERO_PANEL = cn(
  CARD_BASE,
  "surface-glass border-border p-4 shadow-elevation2 animate-rise",
);

export const STRATEGY_SUCCESS_MESSAGE =
  "flex items-start gap-2 rounded-md border border-success/35 bg-success/10 p-3 text-xs text-success dark:border-success/35 dark:bg-success/18 dark:text-success";

export const STRATEGY_RESOLUTION_NOTE = "mt-1 text-[11px] text-success dark:text-success";

export const KPI_VALUE = "text-xl font-semibold tabular-nums text-foreground";
export const KPI_VALUE_ANIMATED = cn(KPI_VALUE, "animate-count-up-fade");
export const KPI_LABEL = "mt-0.5 line-clamp-2 max-w-full px-0.5 text-center text-[11px] leading-tight text-muted-foreground";
export const STEP_BADGE_BASE = "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide";
export const STEP_BADGE_VARIANT = {
  done: "bg-success/20 text-success border border-success/25 dark:bg-success/25 dark:text-success dark:border-success/30",
  active: "bg-primary/15 text-primary border border-primary/25 dark:bg-primary/20 dark:text-primary dark:border-primary/30",
  pending: "bg-muted text-muted-foreground border border-border dark:bg-muted dark:text-muted-foreground dark:border-border",
} as const;
export const STATUS_BADGE_BASE =
  "inline-flex max-w-full shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium";

/** High-contrast tinted surfaces for badges/chips — token semantic tones. */
export const SEMANTIC_TINT_SURFACE = {
  success: "border-success/30 bg-success/12 text-success dark:border-success/30 dark:bg-success/20 dark:text-success",
  warning: "border-warning/30 bg-warning/12 text-warning dark:border-warning/30 dark:bg-warning/20 dark:text-warning",
  info: "border-info/30 bg-info/12 text-info dark:border-info/30 dark:bg-info/20 dark:text-info",
  destructive: "border-destructive/30 bg-destructive/12 text-destructive dark:border-destructive/30 dark:bg-destructive/20 dark:text-destructive",
  muted: "border-border bg-muted text-muted-foreground dark:border-border dark:bg-muted dark:text-muted-foreground",
} as const;

export type SemanticTintTone = keyof typeof SEMANTIC_TINT_SURFACE;

export function semanticTintBadgeClass(tone: SemanticTintTone): string {
  return cn(STATUS_BADGE_BASE, SEMANTIC_TINT_SURFACE[tone]);
}

export function semanticTintSurfaceClass(tone: SemanticTintTone): string {
  return SEMANTIC_TINT_SURFACE[tone];
}

export const SEMANTIC_TINT_PANEL = {
  success: "border-success/30 bg-success/10 text-success dark:border-success/30 dark:bg-success/18 dark:text-success",
  warning: "border-warning/30 bg-warning/10 text-warning dark:border-warning/30 dark:bg-warning/18 dark:text-warning",
  info: "border-info/30 bg-info/10 text-info dark:border-info/30 dark:bg-info/18 dark:text-info",
  destructive: "border-destructive/30 bg-destructive/10 text-destructive dark:border-destructive/30 dark:bg-destructive/18 dark:text-destructive",
} as const;

/** Classification hue tints — paired light/dark text on tinted backgrounds. */
export const PALETTE_TINT_SURFACE = {
  amber: "border-amber-500/30 bg-amber-500/12 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/22 dark:text-amber-200",
  emerald: "border-emerald-500/30 bg-emerald-500/12 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/22 dark:text-emerald-200",
  sky: "border-sky-500/30 bg-sky-500/12 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/22 dark:text-sky-200",
  violet: "border-violet-500/30 bg-violet-500/12 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/22 dark:text-violet-200",
  orange: "border-orange-500/30 bg-orange-500/12 text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/22 dark:text-orange-200",
  red: "border-red-500/30 bg-red-500/12 text-red-700 dark:border-red-500/30 dark:bg-red-500/22 dark:text-red-200",
  slate: "border-slate-500/30 bg-slate-500/12 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/22 dark:text-slate-200",
  teal: "border-teal-500/30 bg-teal-500/12 text-teal-700 dark:border-teal-500/30 dark:bg-teal-500/22 dark:text-teal-200",
  cyan: "border-cyan-500/30 bg-cyan-500/12 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/22 dark:text-cyan-200",
  purple: "border-purple-500/30 bg-purple-500/12 text-purple-700 dark:border-purple-500/30 dark:bg-purple-500/22 dark:text-purple-200",
  yellow: "border-yellow-500/30 bg-yellow-500/12 text-yellow-700 dark:border-yellow-500/30 dark:bg-yellow-500/22 dark:text-yellow-200",
  fuchsia: "border-fuchsia-500/30 bg-fuchsia-500/12 text-fuchsia-700 dark:border-fuchsia-500/30 dark:bg-fuchsia-500/22 dark:text-fuchsia-200",
  green: "border-green-500/30 bg-green-500/12 text-green-700 dark:border-green-500/30 dark:bg-green-500/22 dark:text-green-200",
} as const;

export type PaletteTintHue = keyof typeof PALETTE_TINT_SURFACE;

export function paletteTintBadgeClass(hue: PaletteTintHue): string {
  return cn(STATUS_BADGE_BASE, PALETTE_TINT_SURFACE[hue]);
}

export function paletteTintSurfaceClass(hue: PaletteTintHue): string {
  return PALETTE_TINT_SURFACE[hue];
}

export function workProjectStatusBadgeClass(value?: string | null): string {
  const normalized = (value || "").trim().toLowerCase();
  if (normalized === "at_risk" || normalized === "blocked") return workStatusBadgeClass("atRisk");
  if (normalized === "active") return workStatusBadgeClass("success");
  if (normalized === "archived" || normalized === "closed") return paletteTintBadgeClass("slate");
  if (normalized === "on_hold" || normalized === "paused") return workStatusBadgeClass("pending");
  if (normalized === "completed" || normalized === "done") return paletteTintBadgeClass("teal");
  if (normalized === "cancelled" || normalized === "canceled") return workStatusBadgeClass("overdue");
  if (normalized === "planning" || normalized === "draft") return workStatusBadgeClass("info");
  return workStatusBadgeClass("neutral");
}

// ---------------------------------------------------------------------------
// Kind-scoped semantic badge registry
// ---------------------------------------------------------------------------
// A single source of truth so the SAME hue never means two different things on
// the same surface. Each KIND owns a distinct color band:
//   - status     → WORK_STATUS_TONE traffic-light (green/amber/red/violet/sky)
//   - confidence → indigo certainty ramp (never green, so it can't read as status)
//   - freshness  → teal (current) / neutral (stale) / rose (failed) / dashed amber (unconfigured)
//   - scope/level→ neutral outline (structural attribute, no semantic color)
// Pages MUST route these kinds through the helpers below instead of redefining
// their own tone maps.

/** Confidence certainty ramp — indigo intensity, deliberately non-status. */
export const CONFIDENCE_TONE = {
  high: "border-indigo-500/35 bg-indigo-500/15 text-indigo-700 dark:border-indigo-500/35 dark:bg-indigo-500/25 dark:text-indigo-200",
  medium: "border-indigo-400/25 bg-indigo-400/10 text-indigo-600 dark:border-indigo-400/25 dark:bg-indigo-400/18 dark:text-indigo-300",
  low: "border-border bg-muted/50 text-muted-foreground",
  unknown: "border-dashed border-border bg-transparent text-muted-foreground",
} as const;

export type ConfidenceTone = keyof typeof CONFIDENCE_TONE;

export function confidenceBadgeClass(confidence?: string | null): string {
  const key = (confidence || "").trim().toLowerCase();
  if (key === "high") return cn(STATUS_BADGE_BASE, CONFIDENCE_TONE.high);
  if (key === "medium" || key === "med") return cn(STATUS_BADGE_BASE, CONFIDENCE_TONE.medium);
  if (key === "low") return cn(STATUS_BADGE_BASE, CONFIDENCE_TONE.low);
  return cn(STATUS_BADGE_BASE, CONFIDENCE_TONE.unknown);
}

/** Data-freshness band — teal current, neutral stale, rose failure, dashed amber when unset. */
export const FRESHNESS_TONE = {
  current: "border-teal-500/35 bg-teal-500/14 text-teal-700 dark:border-teal-500/35 dark:bg-teal-500/22 dark:text-teal-200",
  stale: "border-border bg-muted/50 text-muted-foreground",
  failed: "border-rose-500/35 bg-rose-500/14 text-rose-700 dark:border-rose-500/35 dark:bg-rose-500/22 dark:text-rose-200",
  unconfigured: "border-dashed border-amber-500/40 bg-transparent text-amber-700 dark:border-amber-500/40 dark:text-amber-200",
} as const;

export type FreshnessTone = keyof typeof FRESHNESS_TONE;

export function freshnessBadgeClass(freshness?: string | null): string {
  const key = (freshness || "").trim().toLowerCase();
  if (key === "current") return cn(STATUS_BADGE_BASE, FRESHNESS_TONE.current);
  if (key === "failed") return cn(STATUS_BADGE_BASE, FRESHNESS_TONE.failed);
  if (key === "unconfigured") return cn(STATUS_BADGE_BASE, FRESHNESS_TONE.unconfigured);
  return cn(STATUS_BADGE_BASE, FRESHNESS_TONE.stale);
}

/** Structural scope/level (company/team/individual/...) — neutral outline, no semantic color. */
export const SCOPE_LEVEL_BADGE =
  "border-border bg-transparent text-muted-foreground dark:border-border dark:text-muted-foreground";

export function scopeLevelBadgeClass(): string {
  return cn(STATUS_BADGE_BASE, "font-medium", SCOPE_LEVEL_BADGE);
}

function normalizeSemanticKey(value?: string | null): string {
  return (value || "").trim().toLowerCase();
}

/** Universal workflow status → traffic-light badge (explicit switch, neutral default). */
export function statusBadgeTone(value?: string | null): WorkStatusTone {
  switch (normalizeSemanticKey(value)) {
    case "active":
    case "assigned":
    case "approved":
    case "completed":
    case "done":
    case "resolved":
    case "published":
    case "synced":
    case "paid":
    case "settled":
    case "settled_external":
    case "fulfilled":
    case "sent":
    case "success":
    case "green":
    case "applied":
    case "bank_matched":
    case "matched":
    case "ok":
    case "ingested":
    case "ready":
    case "connected":
      return "success";
    case "in_progress":
    case "inprogress":
    case "issued":
    case "submitted":
    case "running":
    case "deploying":
    case "starting":
    case "sending":
    case "processing":
      return "inProgress";
    case "draft":
    case "pending":
    case "open":
    case "on_hold":
    case "paused":
    case "planning":
    case "review":
    case "queued":
    case "stopping":
    case "denied":
    case "partially_matched":
    case "voucher":
    case "unpaid":
      return "pending";
    case "scheduled":
      return "info";
    case "at_risk":
    case "blocked":
    case "conflict":
    case "degraded":
      return "atRisk";
    case "failed":
    case "rejected":
    case "cancelled":
    case "canceled":
    case "void":
    case "terminated":
    case "unhealthy":
    case "error":
    case "down":
    case "offline":
    case "overdue":
    case "red":
    case "suspended":
      return "overdue";
    case "stopped":
    case "closed":
    case "archived":
    case "inactive":
    case "amber":
    case "flat":
    case "steady":
    case "unknown":
      return "neutral";
    default:
      return "neutral";
  }
}

export function statusBadgeClass(value?: string | null): string {
  return workStatusBadgeClass(statusBadgeTone(value));
}

export function statusDotClass(value?: string | null): string {
  return WORK_STATUS_TONE[statusBadgeTone(value)].strip;
}

/** Service/system health → traffic-light badge. */
export function healthBadgeTone(value?: string | null): WorkStatusTone {
  switch (normalizeSemanticKey(value)) {
    case "healthy":
    case "operational":
      return "success";
    case "degraded":
    case "warning":
      return "pending";
    case "down":
    case "offline":
    case "unhealthy":
      return "overdue";
    default:
      return "neutral";
  }
}

export function healthBadgeClass(value?: string | null): string {
  return workStatusBadgeClass(healthBadgeTone(value));
}

/** Deployment environment classification — not brand primary. */
export function environmentBadgeClass(value?: string | null): string {
  switch (normalizeSemanticKey(value)) {
    case "production":
      return paletteTintBadgeClass("red");
    case "staging":
      return paletteTintBadgeClass("amber");
    case "development":
      return paletteTintBadgeClass("cyan");
    case "preview":
      return paletteTintBadgeClass("violet");
    default:
      return workStatusBadgeClass("neutral");
  }
}

/** Alert/guardrail severity ramp. */
export function severityBadgeClass(value?: string | null): string {
  switch (normalizeSemanticKey(value)) {
    case "low":
      return paletteTintBadgeClass("emerald");
    case "medium":
      return paletteTintBadgeClass("amber");
    case "high":
      return paletteTintBadgeClass("orange");
    case "critical":
      return semanticTintBadgeClass("destructive");
    default:
      return workStatusBadgeClass("neutral");
  }
}

/** Metric trend direction. */
export function trendBadgeClass(value?: string | null): string {
  switch (normalizeSemanticKey(value)) {
    case "improving":
    case "up":
      return workStatusBadgeClass("success");
    case "worsening":
    case "down":
    case "declining":
      return workStatusBadgeClass("overdue");
    default:
      return workStatusBadgeClass("neutral");
  }
}

/** Neutral outline for IDs, counts, flags, tags — never brand blue. */
export const METADATA_CHIP =
  "border-border bg-transparent text-muted-foreground dark:border-border dark:text-muted-foreground";

export function metadataChipClass(): string {
  return cn(STATUS_BADGE_BASE, "font-medium", METADATA_CHIP);
}

/** Numeric score/similarity text hue (0..1). */
export function scoreToneClass(score: number): string {
  if (score >= 0.75) return "text-emerald-600 dark:text-emerald-200";
  if (score >= 0.45) return "text-amber-600 dark:text-amber-200";
  return "text-destructive dark:text-destructive";
}

/** Numeric score/similarity badge (0..1). */
export function scoreHealthBadgeClass(score: number): string {
  if (score >= 0.75) return workStatusBadgeClass("success");
  if (score >= 0.45) return workStatusBadgeClass("pending");
  return workStatusBadgeClass("overdue");
}

/** Numeric score/similarity metric card surface (0..1). */
export function scoreHealthMetricCardClass(score: number): string {
  if (score >= 0.75) return workStatusMetricCardClass("success");
  if (score >= 0.45) return workStatusMetricCardClass("pending");
  return workStatusMetricCardClass("overdue");
}

/** Age-in-days staleness badge for review/action items. */
export function stalenessDaysBadgeClass(days: number | null): string {
  if (days === null) return workStatusBadgeClass("neutral");
  if (days > 7) return workStatusBadgeClass("overdue");
  if (days > 3) return workStatusBadgeClass("pending");
  return workStatusBadgeClass("success");
}

/** Type/category hue alias — DB type, model type, mail direction, etc. */
export function typeCategoryBadgeClass(hue: PaletteTintHue): string {
  return paletteTintBadgeClass(hue);
}

/** Pipeline trace stage (done vs pending). */
export function pipelineStageBadgeClass(status: "done" | "pending"): string {
  if (status === "done") return paletteTintBadgeClass("emerald");
  return paletteTintBadgeClass("slate");
}

/** External sync state for task integrations. */
export function syncStateBadgeClass(syncState: string): string {
  switch (normalizeSemanticKey(syncState)) {
    case "synced":
      return statusBadgeClass("synced");
    case "conflict":
      return statusBadgeClass("conflict");
    case "error":
      return statusBadgeClass("error");
    case "pending":
      return statusBadgeClass("pending");
    case "unlinked":
      return paletteTintBadgeClass("slate");
    default:
      return workStatusBadgeClass("neutral");
  }
}

/** Customer connector channel status. */
export function connectorStatusBadgeClass(kind: "connected" | "waiting" | "ignored" | "error"): string {
  switch (kind) {
    case "connected":
      return statusBadgeClass("connected");
    case "ignored":
      return workStatusBadgeClass("pending");
    case "error":
      return statusBadgeClass("error");
    default:
      return workStatusBadgeClass("neutral");
  }
}

/** Customer insight/lifecycle tone badge. */
export function customerInsightBadgeClass(tone: "success" | "warning" | "info" | "default"): string {
  switch (tone) {
    case "success":
      return workStatusBadgeClass("success");
    case "warning":
      return workStatusBadgeClass("pending");
    case "info":
      return workStatusBadgeClass("info");
    default:
      return workStatusBadgeClass("neutral");
  }
}

/** Time-log action event badge. */
export function timeLogActionBadgeClass(value?: string | null): string {
  const normalized = normalizeSemanticKey(value);
  if (normalized.includes("completed")) return statusBadgeClass("completed");
  if (normalized.includes("started") || normalized.includes("resumed")) return workStatusBadgeClass("inProgress");
  if (normalized.includes("paused")) return statusBadgeClass("paused");
  if (normalized.includes("claimed")) return workStatusBadgeClass("info");
  if (normalized.includes("manual")) return paletteTintBadgeClass("cyan");
  return workStatusBadgeClass("neutral");
}

/** Time-log status badge. */
export function timeLogStatusBadgeClass(value?: string | null): string {
  switch (normalizeSemanticKey(value)) {
    case "done":
      return statusBadgeClass("done");
    case "in_progress":
      return workStatusBadgeClass("inProgress");
    case "assigned":
    case "claimed":
      return workStatusBadgeClass("info");
    case "re_assigned":
      return workStatusBadgeClass("atRisk");
    case "new":
    case "pending":
      return statusBadgeClass("pending");
    case "rejected":
      return statusBadgeClass("rejected");
    default:
      return workStatusBadgeClass("neutral");
  }
}

/** Inbox sync job progress status. */
export function syncJobBadgeClass(status: "queued" | "running" | "completed" | "failed" | "cancelled"): string {
  switch (status) {
    case "completed":
      return statusBadgeClass("completed");
    case "failed":
    case "cancelled":
      return statusBadgeClass("failed");
    case "queued":
      return statusBadgeClass("queued");
    default:
      return statusBadgeClass("running");
  }
}

export const CAPACITY_BAR_BASE = "h-1.5 rounded-full overflow-hidden bg-muted/50 min-w-[48px]";
export const CAPACITY_BAR_FILL = {
  ok: "bg-success",
  near: "bg-warning",
  overload: "bg-destructive",
} as const;

/** Scroll + frame for list native `<table>` (inbox, records, finance/ops lists, etc.). */
export const LIST_TABLE_SCROLL_WRAP = "table-surface-frame min-h-0 flex-1 overflow-auto rounded-md border";

/** Same frame as {@link LIST_TABLE_SCROLL_WRAP} but horizontal scroll only (parent handles vertical). */
export const LIST_TABLE_SCROLL_WRAP_HORIZONTAL = "table-surface-frame min-h-0 flex-1 overflow-x-auto rounded-md border";

/** Scroll only, no frame — use when parent already provides border or card chrome. */
export const LIST_TABLE_SHELL_MINIMAL = "min-w-0 overflow-x-auto";

/** When `stickyHeader` on ScrollableTable: pins thead inside the scroll container with a distinct header band. */
export const LIST_TABLE_STICKY_HEAD_WRAP =
  "[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:border-b [&_thead]:border-border [&_thead]:shadow-[0_2px_12px_-6px_rgb(15_23_42_/_0.32)] dark:[&_thead]:shadow-[0_2px_8px_-2px_rgb(0_0_0_/_0.55)] supports-[backdrop-filter]:[&_thead]:backdrop-blur-sm [&_thead_th]:font-semibold";

/** Apply on `<table>` (TokenTable) so header row matches {@link LIST_TABLE_THEAD_ROW_MUTED} without per-page `<tr>` classes. */
export const LIST_TABLE_THEAD_TABLE_TONE =
  "[&_thead>tr]:border-b [&_thead>tr]:border-border [&_thead_th]:font-semibold";

/** Pass to `@/components/table/ScrollableTable` `classes` prop (platform list frame tokens). */
export const listScrollableTableClasses = {
  framed: LIST_TABLE_SCROLL_WRAP,
  framedHorizontal: LIST_TABLE_SCROLL_WRAP_HORIZONTAL,
  minimal: LIST_TABLE_SHELL_MINIMAL,
  stickyHead: LIST_TABLE_STICKY_HEAD_WRAP,
  footer: "table-surface-frame rounded-b-md border border-t-0 px-3 py-1.5",
} as const;

/** Preset min-widths for list `<table>` (full Tailwind class strings so JIT picks them up). Empty string = no min-width token. */
export const LIST_TABLE_MIN_WIDTH_CLASS = {
  draftsList: "min-w-[1100px]",
  recordsExplorer: "min-w-[1280px]",
  dashboardProjectOverview: "min-w-[360px]",
  dashboardMyRespMini: "min-w-[260px]",
  dashboardTaskMatrix: "min-w-[240px]",
  dashboardResource: "min-w-[280px]",
  adminGrid: "",
  adminAuditLog: "min-w-[920px]",
  aiAudit: "min-w-[1280px]",
  llmPrivacyRules: "min-w-[720px]",
  notificationTaskLinks: "min-w-[520px]",
  projectsList: "min-w-[1280px]",
  projectTaskMatrix: "min-w-[200px]",
  ruleContract: "min-w-[1280px]",
  ruleContractSqlEditor: "min-w-[920px]",
  syncHistory: "min-w-[1200px]",
  tasksList: "",
} as const;

export type TableMinWidthPreset = keyof typeof LIST_TABLE_MIN_WIDTH_CLASS;

export type TableElementOptions = {
  tableLayout?: "fixed";
  extraClassName?: string;
};

export function listTableElementClass(preset: TableMinWidthPreset, textSize: "xs" | "sm", options?: TableElementOptions): string {
  const mw = LIST_TABLE_MIN_WIDTH_CLASS[preset];
  // List/report SoT = "xs" (DraftsList / dashboard widgets). Keep "sm" only for rare prose-heavy grids.
  return cn("w-full border-collapse caption-bottom", textSize === "xs" ? "text-xs" : "text-sm", options?.tableLayout === "fixed" && "table-fixed", mw || undefined, options?.extraClassName);
}

/** Default `<thead><tr>` — darker band + bottom rule to separate header from body rows. */
export const LIST_TABLE_THEAD_ROW_MUTED = "border-b border-border";

/** Optional `<th>` label tone when a page does not set its own header cell classes. */
export const LIST_TABLE_TH_LABEL = "font-semibold text-foreground/90";

/**
 * Single-line list cells — compact finance/ops density (text-xs + TABLE_CELL).
 * One field per cell; truncate long business labels. Do not bump to text-sm on list pages.
 */
export const LIST_TABLE_CELL = cn(SPACING.TABLE_CELL, "truncate align-middle text-xs leading-5 text-foreground");
export const LIST_TABLE_CELL_MUTED = cn(SPACING.TABLE_CELL, "truncate align-middle text-xs leading-5 text-muted-foreground");
export const LIST_TABLE_CELL_NUM = cn(
  SPACING.TABLE_CELL,
  "truncate align-middle text-right text-xs leading-5 tabular-nums text-foreground",
);
export const LIST_TABLE_TH = cn(SPACING.TABLE_CELL, "text-left text-xs font-medium leading-5 text-muted-foreground");
export const LIST_TABLE_TH_NUM = cn(SPACING.TABLE_CELL, "text-right text-xs font-medium leading-5 text-muted-foreground");
export const LIST_TABLE_CELL_ACTION = cn(SPACING.TABLE_CELL, "align-middle");
export const LIST_TABLE_ROW = "border-b border-border/70 transition-colors hover:bg-muted/40";
export const LIST_TABLE_ROW_SELECTED = "bg-primary/10";

/** List footer KPI density — Bank transactions footer is SoT (not card-scale KPI_VALUE). */
export const LIST_KPI_STRIP =
  "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/70 pt-2";
export const LIST_KPI_VALUE = "text-sm font-semibold tabular-nums tracking-tight text-foreground";
export const LIST_KPI_LABEL = "text-[11px] text-muted-foreground";
export const LIST_KPI_PAIR = "inline-flex min-w-0 items-center gap-1.5";
export const LIST_KPI_ICON = "h-3.5 w-3.5 shrink-0 text-muted-foreground";
export const LIST_KPI_SEP = "text-border/80";
/** Same string as {@link LIST_TABLE_FOOTER_STACK} — do not redefine. */
export const LIST_KPI_FOOTER_STACK = LIST_TABLE_FOOTER_STACK;

/** Platform list footer KPI chrome — use on any hub list with Bank-style baseline metrics. */
export const listKpiChrome = {
  strip: LIST_KPI_STRIP,
  value: LIST_KPI_VALUE,
  label: LIST_KPI_LABEL,
  pair: LIST_KPI_PAIR,
  icon: LIST_KPI_ICON,
  sep: LIST_KPI_SEP,
  footerStack: LIST_KPI_FOOTER_STACK,
} as const;
